import React, { useOptimistic, useState, useTransition } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FileText, GripVertical, Phone, Save, Star, Users, Video, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from '@clareefai/ui';
import {
  normalizePromoterActions,
  type PromoterActionSetting,
  type PromoterActionType,
} from '@clareefai/types';
import { useGetVendorMeQuery, useUpdateVendorMutation } from '../../store/vendorApi';

const ACTION_STYLE: Record<PromoterActionType, { icon: LucideIcon; color: string; bgColor: string }> = {
  USE_CASE: { icon: FileText, color: 'text-indigo-600', bgColor: 'bg-indigo-50 border-indigo-200' },
  REVIEW: { icon: Star, color: 'text-yellow-600', bgColor: 'bg-yellow-50 border-yellow-200' },
  REFERENCE_CALL: { icon: Phone, color: 'text-blue-600', bgColor: 'bg-blue-50 border-blue-200' },
  VIDEO: { icon: Video, color: 'text-purple-600', bgColor: 'bg-purple-50 border-purple-200' },
  REFERRAL: { icon: Users, color: 'text-green-600', bgColor: 'bg-green-50 border-green-200' },
};

interface SortableActionRowProps {
  action: PromoterActionSetting;
  position: number;
  onToggle: (type: PromoterActionType) => void;
}

const SortableActionRow: React.FC<SortableActionRowProps> = ({ action, position, onToggle }) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: action.type,
  });
  const style = ACTION_STYLE[action.type];
  const Icon = style.icon;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative flex items-center gap-3 p-4 rounded-lg border select-none ${
        action.enabled ? style.bgColor : 'bg-gray-50 border-gray-200 opacity-60'
      } ${isDragging ? 'z-10 shadow-lg ring-2 ring-[#4339A0]/30' : ''}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={t('settings.promoterActions.dragToReorder')}
        title={t('settings.promoterActions.dragToReorder')}
        className="p-1.5 -ml-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-white/70 cursor-grab active:cursor-grabbing touch-none"
      >
        <GripVertical className="w-5 h-5" />
      </button>

      <span className="w-6 text-sm font-semibold text-gray-400 text-center">{position}</span>
      <Icon className={`w-6 h-6 flex-shrink-0 ${action.enabled ? style.color : 'text-gray-400'}`} />
      <span className="flex-1 font-medium text-gray-900">
        {t(`settings.promoterActions.actions.${action.type}`)}
      </span>

      <button
        type="button"
        role="switch"
        aria-checked={action.enabled}
        onClick={() => onToggle(action.type)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
          action.enabled ? 'bg-[#4339A0]' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            action.enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
};

const PromoterActionsSettings: React.FC = () => {
  const { t } = useTranslation();
  const { data: vendor, isLoading } = useGetVendorMeQuery();
  const [updateVendor] = useUpdateVendorMutation();
  const [isSaving, startSaving] = useTransition();

  const saved = normalizePromoterActions(vendor?.promoterActions);
  // Optimistic layer over the saved config: while the mutation and cache refresh
  // are in flight the UI keeps showing the just-saved list instead of snapping
  // back to the previous one.
  const [optimisticSaved, setOptimisticSaved] = useOptimistic(saved);
  // Local uncommitted edits; null = mirror the saved config
  const [draft, setDraft] = useState<PromoterActionSetting[] | null>(null);
  const actions = draft ?? optimisticSaved;
  const hasChanges = draft !== null && JSON.stringify(draft) !== JSON.stringify(saved);

  const sensors = useSensors(
    // Small activation distance keeps plain clicks (e.g. on the toggle) from starting a drag
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = actions.findIndex((a) => a.type === active.id);
    const newIndex = actions.findIndex((a) => a.type === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setDraft(arrayMove(actions, oldIndex, newIndex));
  };

  const toggle = (type: PromoterActionType) => {
    const target = actions.find((a) => a.type === type);
    if (target?.enabled && actions.filter((a) => a.enabled).length === 1) {
      toast.error(t('settings.promoterActions.atLeastOne'));
      return;
    }
    setDraft(actions.map((a) => (a.type === type ? { ...a, enabled: !a.enabled } : a)));
  };

  const handleSave = () => {
    const next = draft;
    if (!next) return;
    startSaving(async () => {
      setOptimisticSaved(next);
      setDraft(null);
      try {
        await updateVendor({ promoterActions: next }).unwrap();
        toast.success(t('settings.promoterActions.saved'));
      } catch {
        // Restore the edits so the user can retry without losing anything
        setDraft(next);
        toast.error(t('settings.promoterActions.saveError'));
      }
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('settings.promoterActions.heading')}</h2>
      <p className="text-gray-600 mb-6">{t('settings.promoterActions.description')}</p>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={actions.map((a) => a.type)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3 mb-4">
            {actions.map((action, index) => (
              <SortableActionRow
                key={action.type}
                action={action}
                position={index + 1}
                onToggle={toggle}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <p className="text-xs text-gray-500 mb-6">{t('settings.promoterActions.onboardingNote')}</p>

      <button
        type="button"
        onClick={handleSave}
        disabled={!hasChanges || isSaving}
        className="flex items-center space-x-2 px-4 py-2 bg-[#4339A0] hover:bg-[#362d80] text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Save className="w-4 h-4" />
        <span>{isSaving ? t('settings.promoterActions.saving') : t('settings.promoterActions.save')}</span>
      </button>
    </div>
  );
};

export default PromoterActionsSettings;
